/**
 * The one place the SPA talks to the API.
 *
 * Every response shape comes from `schema.d.ts`, which is generated from the API's own
 * OpenAPI document — so a route that changes shape breaks the build rather than a page.
 */

import type { components, paths } from "./schema";

export type Schemas = components["schemas"];
export type Paths = paths;

/** Thrown for any non-2xx response. Carries what the API said, not a generic message. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Per-field errors from a 422, keyed by the manifest field name. */
    readonly fields: Record<string, string[]> = {},
    readonly retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** A 404 on a project we cannot see: the API does not distinguish, and neither do we. */
  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

type Query = Record<string, string | number | boolean | undefined | null>;

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: Query): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const serialised = params.toString();
  return serialised ? `${path}?${serialised}` : path;
}

/**
 * Pull a usable message out of whatever the API returned.
 *
 * FastAPI's validation errors and our own hand-written details have different shapes,
 * and a user should never see either raw.
 */
function describe(status: number, payload: unknown): { message: string; fields: Record<string, string[]> } {
  if (typeof payload === "string" && payload) {
    return { message: payload, fields: {} };
  }

  if (payload && typeof payload === "object" && "detail" in payload) {
    const detail = (payload as { detail: unknown }).detail;

    if (typeof detail === "string") {
      return { message: detail, fields: {} };
    }

    // Our own per-field shape: {detail: {detail: "...", fields: {...}}}
    if (detail && typeof detail === "object" && "fields" in detail) {
      const inner = detail as { detail?: string; fields?: Record<string, string[]> };
      return { message: inner.detail ?? "Some values were rejected.", fields: inner.fields ?? {} };
    }

    // FastAPI's own request-validation shape: a list of {loc, msg}.
    if (Array.isArray(detail)) {
      const fields: Record<string, string[]> = {};
      for (const item of detail as Array<{ loc?: unknown[]; msg?: string }>) {
        const name = String(item.loc?.at(-1) ?? "request");
        (fields[name] ??= []).push(item.msg ?? "is not valid");
      }
      const first = Object.entries(fields)[0];
      return {
        message: first ? `${first[0]}: ${first[1][0]}` : "The request was not valid.",
        fields,
      };
    }
  }

  return { message: `Something went wrong (HTTP ${status}).`, fields: {} };
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, query, signal } = options;

  const response = await fetch(buildUrl(path, query), {
    method,
    // The session is a cookie, so every request has to carry it.
    credentials: "same-origin",
    headers: body === undefined ? {} : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  let payload: unknown = text;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      // Leave it as text; `describe` handles both.
    }
  }

  if (!response.ok) {
    const { message, fields } = describe(response.status, payload);
    const retryAfter = response.headers.get("retry-after");
    throw new ApiError(
      response.status,
      message,
      fields,
      retryAfter ? Number(retryAfter) : undefined,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, query?: Query, signal?: AbortSignal) =>
    request<T>(path, { query, signal }),
  post: <T>(path: string, body?: unknown, query?: Query) =>
    request<T>(path, { method: "POST", body, query }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

/** Send the browser to sign in, returning here afterwards. */
export function signIn(returnTo: string = window.location.pathname): void {
  window.location.href = `/api/auth/login?next=${encodeURIComponent(returnTo)}`;
}
